import { cloudinaryConfig } from "../data/configs";
import { uploadWithRetry } from "./uploadWithRetry";
import type {
  CloudinaryUploadResult,
  UploadBatchHandlers,
  UploadJob,
} from "../types/submission";

const CONCURRENCY = 3;

interface WorkerUploadingMessage {
  type: "UPLOADING";
  jobId: number;
  index: number;
  alias: string;
}

interface WorkerSuccessMessage {
  type: "SUCCESS";
  jobId: number;
  index: number;
  alias: string;
  data: CloudinaryUploadResult;
}

interface WorkerErrorMessage {
  type: "ERROR";
  jobId: number;
  index: number;
  alias: string;
  error: string;
}

interface WorkerBatchCompleteMessage {
  type: "BATCH_COMPLETE";
  jobId: number;
}

type WorkerMessage =
  | WorkerUploadingMessage
  | WorkerSuccessMessage
  | WorkerErrorMessage
  | WorkerBatchCompleteMessage;

let workerInstance: Worker | null = null;
let jobCounter = 0;

function getWorker(): Worker | null {
  if (typeof Worker === "undefined") return null;

  if (!workerInstance) {
    workerInstance = new Worker(
      new URL("../workers/upload.worker.ts", import.meta.url)
    );
  }

  return workerInstance;
}

async function runOnMainThread(
  jobs: UploadJob[],
  handlers: UploadBatchHandlers
) {
  const queue = [...jobs];
  const executing: Promise<void>[] = [];

  const runJob = async (job: UploadJob) => {
    handlers.onItemUploading?.(job.index, job.alias);

    try {
      const data = await uploadWithRetry(job.file);
      handlers.onItemSuccess?.(job.index, job.alias, data);
    } catch (err) {
      handlers.onItemError?.(
        job.index,
        job.alias,
        err instanceof Error ? err : new Error("Upload failed")
      );
    }
  };

  while (queue.length > 0 || executing.length > 0) {
    while (executing.length < CONCURRENCY && queue.length > 0) {
      const job = queue.shift()!;
      const promise = runJob(job).finally(() => {
        const index = executing.indexOf(promise);
        if (index !== -1) executing.splice(index, 1);
      });
      executing.push(promise);
    }

    if (executing.length > 0) {
      await Promise.race(executing);
    }
  }
}

export function uploadBatchInBackground(
  jobs: UploadJob[],
  handlers: UploadBatchHandlers
): Promise<void> {
  const worker = getWorker();

  if (!worker || jobs.length === 0) {
    return runOnMainThread(jobs, handlers);
  }

  const jobId = ++jobCounter;

  return new Promise((resolve, reject) => {
    const handleMessage = (event: MessageEvent<WorkerMessage>) => {
      const message = event.data;
      if (message.jobId !== jobId) return;

      switch (message.type) {
        case "UPLOADING":
          handlers.onItemUploading?.(message.index, message.alias);
          break;
        case "SUCCESS":
          handlers.onItemSuccess?.(message.index, message.alias, message.data);
          break;
        case "ERROR":
          handlers.onItemError?.(
            message.index,
            message.alias,
            new Error(message.error)
          );
          break;
        case "BATCH_COMPLETE":
          worker.removeEventListener("message", handleMessage);
          worker.removeEventListener("error", handleError);
          resolve();
          break;
        default:
          break;
      }
    };

    const handleError = (error: ErrorEvent) => {
      worker.removeEventListener("message", handleMessage);
      worker.removeEventListener("error", handleError);
      reject(error);
    };

    worker.addEventListener("message", handleMessage);
    worker.addEventListener("error", handleError);

    worker.postMessage({
      type: "BATCH_UPLOAD",
      jobId,
      jobs,
      config: cloudinaryConfig,
      concurrency: CONCURRENCY,
    });
  });
}

export function terminateUploadPool() {
  if (workerInstance) {
    workerInstance.terminate();
    workerInstance = null;
  }
}
